// IP PBX pages (docs/PBX.md): extensions and their phones, groups, park, conferences, voicemail boxes,
// class of service, feature codes, phone provisioning, the PBX status page and SIP bans.
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Copy, RefreshCw, Trash2 } from 'lucide-react';
import { ResourcePage } from '@/components/ResourcePage';
import { Button } from '@/components/ui/button';
import { Input, Select } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ErrorBox, DataTable, PageHeader, Stat } from '@/components/common';
import { api, errorText } from '@/lib/api';
import { useRefOptions } from '@/hooks/meta';
import { toast } from '@/store/toast';
import { useCan } from '@/store/auth';
import { t } from '@/i18n';
import { fmtDuration, fmtTime, enumLabel } from '@/lib/utils';
import { usePbxNumbers, targetText } from '@/components/PbxPickers';
import { ImportButton } from '@/components/ImportDialog';

const yes = (v: unknown) => Number(v) === 1 || v === true;
const onOff = (v: unknown) => (yes(v) ? <Badge variant="warning">{t('on')}</Badge> : <span className="text-muted-foreground">{t('off')}</span>);
const reg = (v: unknown) => (yes(v) ? <Badge variant="success">{t('registered')}</Badge> : <Badge variant="secondary">{t('offline')}</Badge>);

// what happens to a call: always / busy / no answer / no phone, in one short line, numbers named
function Forwards({ r }: { r: any }) {
  const { lists } = usePbxNumbers();
  const to = (v: string) => targetText(v, lists);
  const parts: string[] = [];
  if (r.fwd_always) parts.push(t('always → {{to}}', { to: to(r.fwd_always) }));
  if (r.fwd_busy) parts.push(t('busy → {{to}}', { to: to(r.fwd_busy) }));
  if (r.fwd_noanswer) parts.push(t('no answer → {{to}}', { to: to(r.fwd_noanswer) }));
  if (r.fwd_unavailable) parts.push(t('no phone → {{to}}', { to: to(r.fwd_unavailable) }));
  return <span className="text-xs">{parts.join(' · ')}</span>;
}

// a number named by what it is: "Extension 2003 · Ann desk", or the outside number as it is
function Target({ v }: { v: unknown }) {
  const { lists } = usePbxNumbers();
  return <>{targetText(v == null ? '' : String(v), lists)}</>;
}

// "TYPE:value" destinations of PBX groups, with the number named
function Destination({ v }: { v: unknown }) {
  const { lists } = usePbxNumbers();
  const m = String(v ?? '').match(/^([A-Z_]+):?(.*)$/);
  if (!m) return <span className="text-muted-foreground">{t('hang up')}</span>;
  return <>{['EXTENSION', 'RING_GROUP', 'CONFERENCE', 'PAGING'].includes(m[1]) ? targetText(m[2], lists) : `${enumLabel(m[1])} ${m[2]}`.trim()}</>;
}

// ---- Extensions -----------------------------------------------------------------------------
export function ExtensionsPage() {
  const [sel, setSel] = React.useState<any | null>(null);
  return (<>
    <ResourcePage path="extensions" title={t('Extensions')}
      description={t('Numbers people dial inside the company. Each extension rings its phones and has forwarding, follow-me and voicemail. A new extension gets a phone (SIP user = its number) and a voicemail box.')}
      columns={['number', 'name', { key: 'dnd', label: t('Do not disturb'), render: (r) => onOff(r.dnd) },
        { key: 'fwd', label: t('Forwarding'), render: (r) => <Forwards r={r} /> },
        { key: 'followme_enabled', label: t('Follow-me'), render: (r) => onOff(r.followme_enabled) },
        { key: 'cos_id', label: t('Class of service'), render: (r) => <RefName path="class-of-service" id={r.cos_id} empty={t('internal only')} /> },
        'max_calls', 'status']}
      headerActions={<ImportButton kind="extensions" title={t('Import extensions')} right="extensions.add" refresh={['extensions', 'users']} />}
      rowActions={(r) => <Button size="sm" variant="ghost" onClick={() => setSel(r)} data-testid={`ext-phones-${r.number}`}>{t('Phones')}</Button>} />
    <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
      {sel && <DialogContent title={t('Extension {{number}} · {{name}}', { number: sel.number, name: sel.name })} className="max-w-3xl">
        <div className="grid gap-6">
          <ResourcePage embedded path="extension-devices" title={t('Phones')} fixed={{ extension_id: sel.id }}
            columns={['label', 'sip_username', 'vendor', 'mac', { key: 'sip_registered', label: t('SIP'), render: (r) => reg(r.sip_registered) }, 'status']} />
          <ResourcePage embedded path="extension-followme" title={t('Follow-me numbers')} fixed={{ extension_id: sel.id }}
            columns={['seq', { key: 'number', label: t('Number'), render: (r) => <Target v={r.number} /> }, 'ring_sec']} />
        </div>
      </DialogContent>}
    </Dialog>
  </>);
}

function RefName({ path, id, empty }: { path: string; id: unknown; empty?: string }) {
  const opts = useRefOptions(path, id !== null && id !== undefined);
  if (id === null || id === undefined) return <span className="text-muted-foreground">{empty ?? ''}</span>;
  const r = (opts.data ?? []).find((o) => Number(o.id) === Number(id));
  return <>{r ? (r.number ? `${r.number} · ${r.name}` : r.name) : `#${id}`}</>;
}

// ---- Phones and provisioning -------------------------------------------------------------------
export function PhonesPage() {
  const mayProvision = useCan('extensions.provision');
  return (<>
    <ResourcePage path="extension-devices" title={t('Phones')}
      description={t('Every SIP account of every extension: desk phones, softphones and browser phones. A phone with a MAC address and a vendor gets its settings from the provisioning address.')}
      columns={[{ key: 'extension_id', label: t('Extension'), render: (r) => <RefName path="extensions" id={r.extension_id} /> }, 'label', 'sip_username', 'vendor', 'model', 'mac',
        { key: 'sip_registered', label: t('SIP'), render: (r) => reg(r.sip_registered) }, 'sip_user_agent',
        { key: 'provisioned_at', label: t('Provisioned'), render: (r) => fmtTime(r.provisioned_at) }, 'status']} />
    {mayProvision && <Provisioning />}
  </>);
}

function Provisioning() {
  const q = useQuery({ queryKey: ['pbx-provisioning'], queryFn: () => api<{ url: string }>('/pbx/provisioning') });
  async function rotate() {
    if (!confirm(t('Make a new provisioning address? Phones set to the old one stop getting their settings.'))) return;
    try { await api('/pbx/provisioning/rotate', { body: {} }); q.refetch(); toast.ok(t('New provisioning address')); } catch (e) { toast.error(errorText(e)); }
  }
  return (
    <Card className="mt-6">
      <CardHeader><CardTitle>{t('Phone provisioning')}</CardTitle></CardHeader>
      <CardContent className="grid gap-2 text-sm">
        <ErrorBox error={q.error} />
        <p>{t('Set this address as the provisioning server (auto-provision URL) on Yealink and Grandstream phones. Each phone asks for its own file by MAC address: Yealink <mac>.cfg, Grandstream cfg<mac>.xml.')}</p>
        <div className="flex items-center gap-2">
          <Input readOnly value={q.data?.url ?? ''} data-testid="prov-url" className="font-mono" />
          <Button size="icon" variant="outline" aria-label={t('Copy')} onClick={() => q.data && navigator.clipboard?.writeText(q.data.url)}><Copy /></Button>
          <Button size="sm" variant="outline" onClick={rotate}><RefreshCw /> {t('New address')}</Button>
        </div>
        <p className="text-muted-foreground">{t('The address holds the SIP passwords of the phones: share it only with whoever sets up the phones.')}</p>
      </CardContent>
    </Card>
  );
}

// ---- Groups, park, conferences ------------------------------------------------------------------
export function RingGroupsPage() {
  const [sel, setSel] = React.useState<any | null>(null);
  return (<>
    <ResourcePage path="ring-groups" title={t('Ring groups')}
      description={t('One number that rings several extensions or outside numbers: all at once, one after the other, or in turn. Nobody answers: the no-answer destination.')}
      columns={['number', 'name', 'strategy', 'ring_sec', { key: 'no_answer_destination', label: t('No answer'), render: (r) => <Destination v={r.no_answer_destination} /> }, { key: 'skip_busy', label: t('Skip busy'), render: (r) => onOff(r.skip_busy) }, 'status']}
      rowActions={(r) => <Button size="sm" variant="ghost" onClick={() => setSel(r)}>{t('Members')}</Button>} />
    <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
      {sel && <DialogContent title={t('Ring group {{number}} · {{name}}', { number: sel.number, name: sel.name })} className="max-w-2xl">
        <ResourcePage embedded path="ring-group-members" title={t('Members')} fixed={{ group_id: sel.id }}
          columns={['seq', { key: 'member', label: t('Member'), render: (r) => (r.extension_id
            ? <>{t('Extension')} <RefName path="extensions" id={r.extension_id} /></> : <>{t('Outside number')} {r.external_number}</>) }]} />
      </DialogContent>}
    </Dialog>
  </>);
}

export function PickupGroupsPage() {
  return <ResourcePage path="pickup-groups" title={t('Pickup groups')}
    description={t("Extensions in the same pickup group answer each other's ringing calls with *8. Put an extension in a group on the Extensions page.")} columns={['name']} />;
}

export function PagingGroupsPage() {
  return <ResourcePage path="paging-groups" title={t('Paging groups')}
    description={t('Dial the number to speak through the speakers of every phone in the group; the phones answer by themselves. Phones on a call or in do not disturb are left out.')}
    columns={['number', 'name', { key: 'extension_ids', label: t('Extensions'), render: (r) => String((r.extension_ids ?? []).length) }, 'status']} />;
}

export function ParkLotsPage() {
  return <ResourcePage path="park-lots" title={t('Call park')}
    description={t('Transfer a call to the park number: it waits with music in the first free slot, which is announced. Dial the slot number from any phone to take the call. After the timeout it rings whoever parked it.')}
    columns={['number', 'name', { key: 'slots', label: t('Slots'), render: (r) => `${r.first_slot}–${r.last_slot}` }, 'timeout_sec', 'status']} />;
}

export function ConferencesPage() {
  return <ResourcePage path="conference-rooms" title={t('Conference rooms')}
    description={t('A number to meet on. With a PIN callers must enter it; the moderator PIN makes a moderator. With wait for moderator, the others hear music until a moderator joins.')}
    columns={['number', 'name', { key: 'pin', label: t('PIN'), render: (r) => (r.pin ? '••••' : '') }, { key: 'wait_for_moderator', label: t('Wait for moderator'), render: (r) => onOff(r.wait_for_moderator) },
      { key: 'record', label: t('Record'), render: (r) => onOff(r.record) }, 'max_members', 'status']} />;
}

export function VoicemailBoxesPage() {
  return <ResourcePage path="voicemail-boxes" title={t('Voicemail boxes')}
    description={t('Mailboxes with a PIN, greetings and e-mail delivery. Every extension gets one with its own number; listen with *97 from the phone or on the My phone page.')}
    columns={['box', 'name', { key: 'extension_id', label: t('Extension'), render: (r) => <RefName path="extensions" id={r.extension_id} /> }, 'email',
      { key: 'email_attach', label: t('Attach'), render: (r) => onOff(r.email_attach) }, 'max_message_sec', 'status']} />;
}

export function ClassOfServicePage() {
  return <ResourcePage path="class-of-service" title={t('Class of service')}
    description={t('What extensions may dial outside: allowed and denied number prefixes (the longer prefix wins; * is any number) and daily limits. An extension without one makes internal calls only. Emergency numbers are always allowed.')}
    columns={['name', 'allow_prefixes', 'deny_prefixes', 'max_calls_per_day', 'max_minutes_per_day', 'status']} />;
}

export const FEATURE_DEFAULTS: [string, string, string][] = [
  ['VOICEMAIL', '*97', 'My voicemail'], ['VOICEMAIL_ANY', '*98', 'Any voicemail box (asks the box)'], ['FWD_ON', '*72', 'Forward all calls (*72 then the number)'],
  ['FWD_OFF', '*73', 'Forwarding off'], ['DND_ON', '*78', 'Do not disturb on'], ['DND_OFF', '*79', 'Do not disturb off'],
  ['FOLLOWME_ON', '*21', 'Follow-me on'], ['FOLLOWME_OFF', '*22', 'Follow-me off'], ['PICKUP_GROUP', '*8', 'Answer a call ringing in my pickup group'],
  ['PICKUP', '**', 'Answer a ringing extension (** then the extension)'], ['INTERCOM', '*80', 'Intercom: the extension answers on speaker (*80 then the extension)'],
  ['SPEED_DIAL', '*0', 'Speed dial (*0 then the slot: *05 calls slot 5)'],
];

// ---- speed dial (2026-10-15) ---------------------------------------------------------------------
function ListOwner({ id }: { id: unknown }) {
  const exts = useRefOptions('extensions');
  if (id == null || id === '') return <span>{t('Company')}</span>;
  const e = (exts.data ?? []).find((x) => Number(x.id) === Number(id));
  return <span>{e ? `${e.number} · ${e.name}` : `#${id}`}</span>;
}
export function SpeedDialPage() {
  const codes = useQuery({ queryKey: ['feature-codes', 'all'], queryFn: () => api<{ rows: { feature: string; code: string }[] }>('/feature-codes', { query: { limit: 100 } }) });
  const code = codes.data?.rows.find((r) => r.feature === 'SPEED_DIAL')?.code ?? '*0';
  return <ResourcePage path="speed-dials" title={t('Speed dial')}
    description={t('Short numbers for numbers dialed often: {{code}} and the slot ({{code}}5 calls slot 5). Company entries work on every phone; an extension\'s own entry wins over the company entry of the same slot.', { code })}
    columns={[{ key: 'slot', label: t('Dial'), render: (r) => <code data-testid={`speed-${r.slot}`}>{`${code}${r.slot}`}</code> }, 'name',
              { key: 'number', label: t('Calls'), render: (r) => <Target v={r.number} /> },
              { key: 'extension_id', label: t('List'), render: (r) => <ListOwner id={r.extension_id} /> }]} />;
}

export function FeatureCodesPage() {
  const q = useQuery({ queryKey: ['feature-codes', 'all'], queryFn: () => api<{ rows: { id: number; feature: string; code: string }[] }>('/feature-codes', { query: { limit: 100 } }) });
  const mine = new Map((q.data?.rows ?? []).map((r) => [r.feature, r]));
  return (<>
    <PageHeader title={t('Feature codes')} description={t('Star codes the phones dial. Change a code to use your own; delete the change to go back to the standard code.')} />
    <DataTable rowKey={(r) => r[0]} rows={FEATURE_DEFAULTS} columns={[
      { key: '0', label: t('Feature'), render: (r) => t(r[2]) },
      { key: '1', label: t('Standard code'), render: (r) => <code>{r[1]}</code> },
      { key: '2', label: t('Code in use'), render: (r) => <code data-testid={`fc-${r[0]}`}>{mine.get(r[0])?.code ?? r[1]}</code> }]} />
    <div className="mt-6"><ResourcePage embedded path="feature-codes" title={t('Your changes')} columns={['feature', 'code']} /></div>
  </>);
}

// ---- PBX status (the dashboard of a PBX tenant) -------------------------------------------------
export function PbxStatusPage() {
  const s = useQuery({ queryKey: ['pbx-status'], queryFn: () => api<{ counts: Record<string, number>; phones: any[] }>('/pbx/status'), refetchInterval: 10_000 });
  const mayReport = useCan('reports.view');
  const c = s.data?.counts ?? {};
  return (<>
    <PageHeader title={t('PBX')} description={t('Extensions, phones and calls right now.')} />
    <ErrorBox error={s.error} />
    <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="pbx-counts">
      <Stat label={t('Extensions')} value={c.extensions ?? '…'} />
      <Stat label={t('Phones registered')} value={`${c.registered ?? '…'} / ${c.devices ?? '…'}`} />
      <Stat label={t('Calls now')} value={c.live_calls ?? '…'} />
      <Stat label={t('New voicemails')} value={c.new_voicemails ?? '…'} />
    </div>
    <h2 className="mb-2 text-lg font-semibold">{t('Phones')}</h2>
    <DataTable rowKey={(r) => r.id} rows={s.data?.phones} columns={[
      { key: 'number', label: t('Extension') }, { key: 'name', label: t('Name') }, { key: 'label', label: t('Label') }, { key: 'sip_username', label: t('SIP user') },
      { key: 'sip_registered', label: t('SIP'), render: (r) => reg(r.sip_registered) }, { key: 'dnd', label: t('Do not disturb'), render: (r) => onOff(r.dnd) },
      { key: 'sip_user_agent', label: t('Phone') }, { key: 'sip_contact', label: t('Address'), render: (r) => <span className="text-xs">{String(r.sip_contact ?? '').replace(/^.*@/, '').replace(/[;>].*$/, '')}</span> }]} />
    {mayReport && <ExtensionReport />}
  </>);
}

function ExtensionReport() {
  const [days, setDays] = React.useState('1');
  const from = new Date(Date.now() - Number(days) * 86400_000).toISOString().slice(0, 16);
  const q = useQuery({ queryKey: ['pbx-report', days], queryFn: () => api<{ rows: any[] }>('/pbx/reports/extensions', { query: { from } }) });
  return (
    <div className="mt-8">
      <div className="mb-2 flex items-center gap-2">
        <h2 className="text-lg font-semibold">{t('Calls per extension')}</h2>
        <Select className="ml-auto w-40" value={days} onChange={(e) => setDays(e.target.value)} aria-label={t('Period')}>
          <option value="1">{t('Last 24 hours')}</option><option value="7">{t('Last 7 days')}</option><option value="30">{t('Last 30 days')}</option>
        </Select>
      </div>
      <ErrorBox error={q.error} />
      <DataTable rowKey={(r) => r.id} rows={q.data?.rows} columns={[
        { key: 'number', label: t('Extension') }, { key: 'name', label: t('Name') },
        { key: 'made', label: t('Made') }, { key: 'made_answered', label: t('Answered (made)') }, { key: 'talk_out_sec', label: t('Talk out'), render: (r) => fmtDuration(r.talk_out_sec) },
        { key: 'received', label: t('Received') }, { key: 'answered', label: t('Answered') }, { key: 'missed', label: t('Missed') },
        { key: 'talk_in_sec', label: t('Talk in'), render: (r) => fmtDuration(r.talk_in_sec) }]} />
    </div>
  );
}

// ---- SIP bans (platform) --------------------------------------------------------------------------
export function SipBansPage() {
  const q = useQuery({ queryKey: ['sip-bans'], queryFn: () => api<{ rows: any[] }>('/pbx/bans'), refetchInterval: 15_000 });
  async function unban(ip: string) {
    try { await api(`/pbx/bans/${encodeURIComponent(ip)}`, { method: 'DELETE' }); toast.ok(t('{{ip}} may sign in again', { ip })); q.refetch(); }
    catch (e) { toast.error(errorText(e)); }
  }
  return (<>
    <PageHeader title={t('SIP security')} description={t('Addresses with failed SIP sign-ins. Too many failures in a short time ban the address for a while (settings pbx_auth_max_failures, pbx_auth_window_sec, pbx_auth_ban_sec).')} />
    <ErrorBox error={q.error} />
    <DataTable rowKey={(r) => r.ip} rows={q.data?.rows} empty={t('No failed sign-ins')} columns={[
      { key: 'ip', label: t('Address') }, { key: 'failures', label: t('Failures') }, { key: 'last_user', label: t('Last user tried') },
      { key: 'banned', label: t('Banned'), render: (r) => (yes(r.banned) ? <Badge variant="destructive">{t('until {{time}}', { time: fmtTime(r.banned_until) })}</Badge> : <span className="text-muted-foreground">{t('no')}</span>) }]}
      actions={(r) => <Button size="sm" variant="ghost" onClick={() => unban(r.ip)}><Trash2 /> {t('Clear')}</Button>} />
  </>);
}

